import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Button, Grid, Header, Message, MessageHeader, Segment } from "semantic-ui-react";
import _ from "lodash";
import moment from "moment";

import SimpleOkCancelModal from "../../../Lumber/SimpleOkCancelModal";
import { useFieldAssociationConfirmation } from "../../../../hooks/useFieldAssociationConfirmation";
import {
	dataLayerDetailsQueryOptions,
	vectorDataLayerUploadsQueryOptions,
	deleteVectorDataLayerUpload,
	fetchFromAzureFileShare,
} from "../../../../apis/dataLayerApi";
import { useAuth0 } from "../../../../auth/auth0";
import { useUserAuth } from "../../../../hooks/useUserAuth";
import VectorUploadRow from "./VectorUploadRow";
import VectorConfiguratorModal, { VectorConfiguratorModalProps } from "./VectorConfiguratorModal";
import type {
	DataLayerOptions,
	DataLayerTypeSelectOption,
	DataLayerUpload,
	PreprocessedFeatureCollectionProps,
	UUID,
	VectorDataLayerUploadPreprocResult,
	VectorDataLayerUploadStdResult,
} from "../../../../apis/types/dataLayerTypes";
import { AerialPlotResponse } from "../../../../apis/types";
import { FIELD_ASSOC_RESET_HEADER, FIELD_ASSOC_RESET_BODY } from "./constants";
import styles from "./styles.module.css";

/**
 * Fn to generate stable query keys for Azure file share Sas Urls. This query key does not fully
 * represent data identity, however manual calls to setQueryData keep it correct in this component.
 */
const generateQueryKeyPreprocGeoJson = (uploadId?: UUID) => ["Fetch preprocessedDataSasUrl", uploadId];

/**
 * Fn to generate stable query keys for Azure file share Sas Urls. This query key does not fully
 * represent data identity, however manual calls to setQueryData keep it correct in this component.
 */
const generateQueryKeyStdGeoJson = (uploadId?: UUID) => ["Fetch stdSasUrl", uploadId];

export interface VectorDataContentProps {
	dataLayerId: string;
	dataLayerOptions: DataLayerOptions | undefined;
	dataLayerHasExistingFieldAssociations: boolean;
	onResetExistingFieldAssociations: () => void;
	onUploadsComplete: (nextComplete: boolean) => void;
	onLoadingChange: (loading: boolean) => void;
}

const VectorDataContent = ({
	dataLayerId,
	dataLayerOptions,
	dataLayerHasExistingFieldAssociations,
	onResetExistingFieldAssociations,
	onUploadsComplete,
	onLoadingChange,
}: VectorDataContentProps) => {
	const { getTokenSilently } = useAuth0() as { getTokenSilently: () => Promise<string> };
	const userAuth = useUserAuth() as { currentClientId: string };
	const queryClient = useQueryClient();

	/*
	 * State
	 */

	const [isDeletingUUID, setIsDeletingUUID] = useState<UUID | undefined>(undefined);
	const [isDeletingAll, setIsDeletingAll] = useState(false);
	const [failedToLoad, setFailedToLoad] = useState(false);
	const [pendingUploads, setPendingUploads] = useState<DataLayerUpload[]>([]);
	const [deleteAllModalIsOpen, setDeleteAllModalIsOpen] = useState(false);

	// state of last upload response and it's result data model
	const [uploadResponse, setUploadResponse] = useState<
		AerialPlotResponse<VectorDataLayerUploadPreprocResult> | undefined
	>(undefined);

	// state of modal configurator dialog and it's open/closed state (undefined: closed)
	const [configuratorModalContent, setConfiguratorModalContent] = useState<DataLayerUpload | undefined>(undefined);

	/*
	 * Queries and Mutations
	 */

	// fetch DataLayer details
	const {
		data: dataLayer,
		error: dataLayerError,
		isPending: dataLayerIsPending,
	} = useQuery(dataLayerDetailsQueryOptions(getTokenSilently, userAuth.currentClientId, dataLayerId));

	// fetch vector data uploads for this data layer
	const {
		data: vectorUploads,
		error: vectorUploadsError,
		refetch: vectorUploadsRefetch,
		isPending: vectorUploadsIsPending,
		isRefetching: vectorUploadsIsRefetching,
	} = useQuery(vectorDataLayerUploadsQueryOptions(getTokenSilently, userAuth.currentClientId, dataLayerId));

	// fetch preprocessed GeoJSON and metadata (same as result of last POST upload/preprocessing request)
	const {
		data: vectorDataLayerUploadPreProcessedGeoJson,
		error: vectorDataLayerUploadPreProcessedGeoJsonError,
		isFetching: vectorDataLayerUploadPreProcessedGeoJsonIsFetching,
	} = useQuery(
		queryOptions({
			enabled: !!configuratorModalContent?.preprocessedDataSasUrl,
			queryKey: generateQueryKeyPreprocGeoJson(configuratorModalContent?.id),
			queryFn: async () => {
				const url = configuratorModalContent?.preprocessedDataSasUrl;
				if (!url) throw new Error("Missing preprocessedDataSasUrl");
				const response = await fetchFromAzureFileShare(url);
				const result = (await response.json()) as VectorDataLayerUploadPreprocResult;
				return result;
			},
		}),
	);

	// fetch standardized GeoJSON and metadata (same as result of last PUT upload/preprocessing request)
	const {
		data: vectorDataLayerUploadStdResultGeoJson,
		error: vectorDataLayerUploadStdResultGeoJsonError,
		isFetching: vectorDataLayerUploadStdResultGeoJsonIsFetching,
	} = useQuery(
		queryOptions({
			enabled: !!configuratorModalContent?.stdDataSasUrl,
			queryKey: generateQueryKeyStdGeoJson(configuratorModalContent?.id),
			queryFn: async () => {
				const url = configuratorModalContent?.stdDataSasUrl;
				if (!url) throw new Error("Missing stdDataSasUrl");
				const response = await fetchFromAzureFileShare(url);
				const result = (await response.json()) as VectorDataLayerUploadStdResult;
				return result;
			},
		}),
	);

	// delete upload mutation fn
	const { mutateAsync: deleteVectorDataLayerUploadMutationAsync } = useMutation({
		mutationFn: deleteVectorDataLayerUpload,
		onSuccess: () => {
			void vectorUploadsRefetch();
		},
		onSettled: () => {
			setIsDeletingUUID(undefined);
		},
	});

	/*
	 * Effects
	 */

	// Generate at least one pending upload record, locally, if there are no uploads yet.
	useEffect(() => {
		if (vectorUploads !== undefined && vectorUploads.length === 0) {
			setPendingUploads((prev) => {
				if (prev.length > 0) {
					return prev;
				}
				return [
					{
						id: `pending-${crypto.randomUUID()}`,
						dataLayerId,
						dataLayerTypeId: "",
						rawDataUrlPath: "",
						createdDateTimeUtc: moment.utc().toDate(),
					},
				];
			});
		}
	}, [vectorUploads, dataLayerId]);

	// Handle dataLayer query failure
	useEffect(() => {
		if (dataLayerError !== null) {
			console.error(dataLayerError);
			toast.error(dataLayerError.message);
			setFailedToLoad(true); // unrecoverable error on page load
		}
	}, [dataLayerError]);

	// Handle vectorUploadsError query failure
	useEffect(() => {
		if (vectorUploadsError !== null) {
			console.error(vectorUploadsError);
			toast.error(vectorUploadsError.message);
			setFailedToLoad(true); // unrecoverable error on page load
		}
	}, [vectorUploadsError]);

	// Handle vectorDataLayerUploadPreProcessedGeoJsonError query failure
	useEffect(() => {
		if (vectorDataLayerUploadPreProcessedGeoJsonError !== null) {
			console.error(vectorDataLayerUploadPreProcessedGeoJsonError);
			toast.error(vectorDataLayerUploadPreProcessedGeoJsonError.message);
		}
	}, [vectorDataLayerUploadPreProcessedGeoJsonError]);

	// Handle vectorDataLayerUploadStdResultGeoJsonError query failure
	useEffect(() => {
		if (vectorDataLayerUploadStdResultGeoJsonError !== null) {
			console.error(vectorDataLayerUploadStdResultGeoJsonError);
			toast.error(vectorDataLayerUploadStdResultGeoJsonError.message);
		}
	}, [vectorDataLayerUploadStdResultGeoJsonError]);

	// Show feedback when settings/edit button is clicked
	useEffect(() => {
		if (configuratorModalContent === undefined) {
			return;
		}
		const toastId = `fetch-last-upload-details-${configuratorModalContent.id}`;
		if (vectorDataLayerUploadPreProcessedGeoJsonIsFetching || vectorDataLayerUploadStdResultGeoJsonIsFetching) {
			toast.loading(`Loading data layer details...`, { toastId, type: "info" });
		} else {
			toast.dismiss(toastId);
		}
	}, [
		configuratorModalContent,
		vectorDataLayerUploadPreProcessedGeoJsonIsFetching,
		vectorDataLayerUploadStdResultGeoJsonIsFetching,
	]);

	/*
	 * Callbacks
	 */

	const {
		fieldAssocModalIsOpen,
		handleFieldAssocModalOk,
		handleFieldAssocModalCancel,
		withFieldAssociationConfirmation,
	} = useFieldAssociationConfirmation(dataLayerHasExistingFieldAssociations, onResetExistingFieldAssociations);

	const handleAdd = useCallback(() => {
		const newVectorUpload: DataLayerUpload = {
			id: `pending-${crypto.randomUUID()}`,
			dataLayerId,
			dataLayerTypeId: "",
			rawDataUrlPath: "",
			createdDateTimeUtc: moment.utc().toDate(),
		};
		setPendingUploads((prev) => [...prev, newVectorUpload]);
	}, [dataLayerId]);

	const handleDelete = useCallback(
		async (upload: DataLayerUpload) => {
			setIsDeletingUUID(upload.id);
			if (vectorUploads?.some((v) => v.id === upload.id)) {
				const toastId = `vector-data-upload-delete-${upload.id}`;
				toast.loading(`Deleting vector data layer upload...`, { toastId, type: "info" });
				const clientId = userAuth.currentClientId;
				try {
					await deleteVectorDataLayerUploadMutationAsync({
						getAccessToken: getTokenSilently,
						clientId,
						dataLayerUploadId: upload.id,
					});
					toast.dismiss(toastId);
				} catch (error) {
					console.error(error);
					toast.dismiss(toastId);
					toast.error(error instanceof Error ? error.message : String(error));
				}
			}
			// handle case of upload.id was in pending uploads (not uploaded to server)
			else if (pendingUploads.some((p) => p.id === upload.id)) {
				setPendingUploads((prev) => prev.filter((p) => p.id !== upload.id));
			}
			setIsDeletingUUID(undefined);
		},
		[
			userAuth.currentClientId,
			vectorUploads,
			pendingUploads,
			getTokenSilently,
			deleteVectorDataLayerUploadMutationAsync,
		],
	);

	// Trigger configurator modal for the specified upload record
	const handleConfigureRequest = useCallback((upload: DataLayerUpload) => {
		// setting this state triggers the GeoJson fetch queries (if applicable)
		setConfiguratorModalContent(upload);
	}, []);

	const handleDeleteAllModalOk = useCallback(async () => {
		setDeleteAllModalIsOpen(false);
		setPendingUploads([]);
		setIsDeletingAll(true);
		const toastId = "vector-data-upload-delete-all";
		try {
			if (vectorUploads) {
				const clientId = userAuth.currentClientId;
				// Optimistically clear the list before mutations run
				queryClient.setQueryData(
					vectorDataLayerUploadsQueryOptions(getTokenSilently, clientId, dataLayerId).queryKey,
					[],
				);
				toast.loading(`Deleting vector data layer upload 1 of ${vectorUploads.length}...`, {
					toastId,
					type: "info",
					autoClose: false,
				});
				for (const [index, upload] of vectorUploads.entries()) {
					toast.update(toastId, {
						render: `Deleting vector data layer upload ${index + 1}/${vectorUploads.length}...`,
						type: "info",
						isLoading: true,
						autoClose: false,
					});
					await deleteVectorDataLayerUpload({
						getAccessToken: getTokenSilently,
						clientId,
						dataLayerUploadId: upload.id,
					});
				}
				void vectorUploadsRefetch();
				toast.dismiss(toastId);
			}
		} catch (error) {
			console.error(error);
			toast.dismiss(toastId);
			toast.error(error instanceof Error ? error.message : String(error));
		} finally {
			setIsDeletingAll(false);
		}
	}, [userAuth.currentClientId, vectorUploads, getTokenSilently, vectorUploadsRefetch, dataLayerId, queryClient]);

	const handleDeleteAllModalRequest = useCallback(() => {
		setDeleteAllModalIsOpen(true);
	}, []);

	/**
	 * Handle cancel button on delete all modal.
	 */
	const handleDeleteAllModalCancel = useCallback(() => {
		setDeleteAllModalIsOpen(false);
	}, []);

	/**
	 * Handle upload (POST) completion callback from VectorDataLayerCtrl component.
	 */
	const handleUploadResponse = useCallback(
		(pendingId: UUID, newId: UUID, response: AerialPlotResponse<VectorDataLayerUploadPreprocResult>) => {
			setPendingUploads((prev) => prev.filter((upload) => upload.id !== pendingId));
			setUploadResponse(response);

			// check if any layers were valid
			const result = (response.data as unknown as { properties: PreprocessedFeatureCollectionProps }).properties;

			if (result.id !== newId) {
				throw Error(`Mismatched uuids in handleUploadResponse ${newId} != ${result.id}`);
			}

			// write this result into the react-query cache, because it's the same content as the fetch of
			// upload.preprocessedDataSasUrl, the alternate ui flow for entering modal.
			const queryKey = generateQueryKeyPreprocGeoJson(newId);
			queryClient.setQueryData(queryKey, response.data);

			if (result.isValid?.some((v) => v)) {
				// trigger the modal content to have new uuid for the upload record target.
				const layerMetadata = (response.data as unknown as { properties: PreprocessedFeatureCollectionProps })
					.properties;
				const tempUploadRec: DataLayerUpload = {
					id: layerMetadata.id,
					dataLayerId: layerMetadata.dataLayerId,
					dataLayerTypeId: layerMetadata.dataLayerTypeId,
					createdDateTimeUtc: layerMetadata.createdDateTimeUtc,
					rawDataUrlPath: "", // not needed for this this temp. record
				};
				setConfiguratorModalContent(tempUploadRec);
			} else {
				// no valid layers detected: reload the uploads query, which will re-generate validation
				// messages display in ui.
				void vectorUploadsRefetch();
			}
		},
		[queryClient, vectorUploadsRefetch],
	);

	/**
	 * Handle configure (PUT) completion callback from ConfiguratorModal component.
	 */
	const handleConfigModalSaved = useCallback(
		(uploadId: UUID, response: AerialPlotResponse<VectorDataLayerUploadPreprocResult>) => {
			// write this result into the react-query cache, because it's the same content as the fetch of
			// upload.stdDataSasUrl, the alternate ui flow for entering modal.
			const queryKey = generateQueryKeyStdGeoJson(uploadId);
			queryClient.setQueryData(queryKey, response.data);

			setConfiguratorModalContent(undefined);
			setUploadResponse(undefined);
			// reload the uploads query, which will re-generate validation messages display in ui.
			void vectorUploadsRefetch();
		},
		[queryClient, vectorUploadsRefetch],
	);

	const handleConfigModalCancelClose = useCallback(() => {
		setConfiguratorModalContent(undefined);
		setUploadResponse(undefined);
	}, []);

	/*
	 * Gated action wrappers — if the data layer has existing field associations,
	 * show a confirmation modal before proceeding with the destructive action.
	 */

	const gatedHandleDelete = useCallback(
		(upload: DataLayerUpload) => {
			withFieldAssociationConfirmation(() => void handleDelete(upload));
		},
		[withFieldAssociationConfirmation, handleDelete],
	);

	const gatedHandleDeleteAllModalRequest = useCallback(() => {
		withFieldAssociationConfirmation(() => handleDeleteAllModalRequest());
	}, [withFieldAssociationConfirmation, handleDeleteAllModalRequest]);

	/*
	 * Derived State & Memoization
	 */

	const anyQueryLoading = useMemo(
		() => vectorUploadsIsPending || dataLayerIsPending,
		[vectorUploadsIsPending, dataLayerIsPending],
	);

	// Combine server data with pending items for vertical segments display
	const allUploads = useMemo(() => [...(vectorUploads || []), ...pendingUploads], [vectorUploads, pendingUploads]);

	// Generate validation message from the state of vectorUpload records (if any)
	const uploadValidationMessages = useMemo(() => {
		if (!vectorUploads || vectorUploads.length === 0) {
			return {};
		}
		if (vectorUploadsIsRefetching) {
			return {};
		}

		const newMsgs: Record<UUID, string> = {};
		for (const u of vectorUploads) {
			let newMsg = "";

			if (uploadResponse) {
				const fcProps = (uploadResponse.data as unknown as { properties: PreprocessedFeatureCollectionProps })
					.properties;
				if (fcProps.id === u.id) {
					// check if any layers are marked as invalid, then append API validation messages
					if (fcProps.isValid?.some((v) => !v)) {
						newMsg += uploadResponse.messages.join(" ");
					}
				}
			}
			// regardless of any uploadResponse, absence of urlPath indicates partial upload, and the
			// standard geojson format was not generated.
			if (u.rawDataUrlPath && !u.urlPath) {
				newMsg += " Upload was not successfully processed. Please edit configuration or delete this upload.";
			}
			newMsgs[u.id] = newMsg;
		}
		return newMsgs;
	}, [vectorUploads, uploadResponse, vectorUploadsIsRefetching]);

	// Generate select menu options, filtered by the dataLayer's group id (ex: Vector Data)
	const dataLayerTypeSelectOptions = useMemo(() => {
		if (!dataLayerOptions || dataLayerOptions.dataLayerTypes.length === 0) {
			return [];
		}
		if (!dataLayer) {
			return [];
		}
		const selectOpts: DataLayerTypeSelectOption[] = dataLayerOptions.dataLayerTypes
			.filter((dlt) => dlt.dataLayerGroupId === dataLayer.dataLayerGroupId)
			.map((dlt) => {
				return {
					key: dlt.id,
					value: dlt.id,
					text: dlt.presetName || dlt.name,
				};
			});
		return selectOpts;
	}, [dataLayerOptions, dataLayer]);

	// For Continue button and Process ribbon navigation, require at least 1 upload, and no validation
	// errors.
	useEffect(() => {
		let result = true;
		if (!vectorUploads || vectorUploads.length === 0) {
			result = false;
		}
		if (_.some(Object.values(uploadValidationMessages), (msg) => msg !== "")) {
			result = false;
		}
		onUploadsComplete(result);
	}, [vectorUploads, uploadValidationMessages, onUploadsComplete]);

	// Memoize the configurator modal props
	const configModalProps = useMemo<VectorConfiguratorModalProps | undefined>(() => {
		if (!configuratorModalContent || !vectorDataLayerUploadPreProcessedGeoJson || !dataLayerOptions || !vectorUploads) {
			return undefined;
		}

		// wait for geojson fetch to complete
		if (!!configuratorModalContent.stdDataSasUrl && vectorDataLayerUploadStdResultGeoJson === undefined) {
			return undefined;
		}

		const layerMetadata = (
			vectorDataLayerUploadPreProcessedGeoJson as unknown as { properties: PreprocessedFeatureCollectionProps }
		).properties;

		const dataLayerTypeId = layerMetadata.dataLayerTypeId;
		const vectorDataType = dataLayerOptions.dataLayerTypes.find((dlt) => dlt.id === dataLayerTypeId);
		if (!vectorDataType) {
			throw Error(`Missing dataLayerTypes record for uuid ${dataLayerTypeId}`);
		}
		const props: VectorConfiguratorModalProps = {
			upload: configuratorModalContent,
			existingUploads: vectorUploads,
			vectorDataType,
			preProcessedGeoJson: vectorDataLayerUploadPreProcessedGeoJson,
			stdGeoJson: vectorDataLayerUploadStdResultGeoJson,
			dataLayerHasExistingFieldAssociations,
			onResetExistingFieldAssociations,
			onSaved: handleConfigModalSaved,
			onClose: handleConfigModalCancelClose,
			onCancel: handleConfigModalCancelClose,
		};
		return props;
	}, [
		configuratorModalContent,
		vectorUploads,
		dataLayerOptions,
		dataLayerHasExistingFieldAssociations,
		onResetExistingFieldAssociations,
		handleConfigModalSaved,
		handleConfigModalCancelClose,
		vectorDataLayerUploadPreProcessedGeoJson,
		vectorDataLayerUploadStdResultGeoJson,
	]);

	useEffect(() => {
		onLoadingChange(anyQueryLoading);
	}, [anyQueryLoading, onLoadingChange]);

	/*
	 * Rendering
	 */

	if (anyQueryLoading) {
		return null;
	}

	if (failedToLoad) {
		return (
			<Segment basic>
				<Message negative>
					<MessageHeader>Error</MessageHeader>
					<p>Error while preparing Vector Data Layer Uploader. Please try again.</p>
				</Message>
			</Segment>
		);
	}

	return (
		<>
			<Header as="h2">Categorical Factors (Vector Data)</Header>
			{allUploads.length > 0 && dataLayerTypeSelectOptions.length > 0 && (
				<div className={styles.uploadRowsList}>
					{allUploads.map((vectorUpload) => (
						<Segment key={vectorUpload.id} vertical basic className={`${styles.segment} ${styles.uploadRowSegment}`}>
							<VectorUploadRow
								clientId={userAuth.currentClientId}
								dataLayerId={dataLayerId}
								dataLayerTypeSelectOptions={dataLayerTypeSelectOptions}
								upload={vectorUpload}
								validationMsg={uploadValidationMessages[vectorUpload.id]}
								onBeforeModify={withFieldAssociationConfirmation}
								onUploadResponse={
									handleUploadResponse as (
										pendingId: UUID,
										newId: UUID,
										response: AerialPlotResponse<GeoJSON.FeatureCollection>,
									) => void
								}
								onDelete={gatedHandleDelete}
								onConfigVectorUpload={handleConfigureRequest}
								isConfiguring={vectorUpload.id === configuratorModalContent?.id}
								isDeleting={isDeletingAll || vectorUpload.id === isDeletingUUID}
								isDisabled={vectorUploadsIsRefetching}
							/>
						</Segment>
					))}
				</div>
			)}
			<Segment vertical basic className={`${styles.segment} ${styles.uploadFooterActions}`}>
				<Grid stackable className={styles.uploadFooterRow}>
					<Grid.Row verticalAlign="middle">
						<Grid.Column computer={6} className={styles.uploadFooterLeftCol}>
							<Button content="Add Vector Data" icon="add" compact onClick={handleAdd} />
						</Grid.Column>
						<Grid.Column computer={10} textAlign="right" className={styles.uploadFooterRightCol}>
							{vectorUploads && vectorUploads.length > 0 && (
								<SimpleOkCancelModal
									headerMsg="Confirm Delete all"
									bodyMsg="All vector data uploads associated with this data layer will be deleted permanently."
									open={deleteAllModalIsOpen}
									onOpen={gatedHandleDeleteAllModalRequest}
									onOk={() => void handleDeleteAllModalOk()}
									onCancel={handleDeleteAllModalCancel}
									trigger={<Button content="Delete All Above Uploads" icon="trash" compact />}
								/>
							)}
						</Grid.Column>
					</Grid.Row>
				</Grid>
			</Segment>

			{configModalProps && <VectorConfiguratorModal {...configModalProps} />}

			<SimpleOkCancelModal
				headerMsg={FIELD_ASSOC_RESET_HEADER}
				bodyMsg={FIELD_ASSOC_RESET_BODY}
				open={fieldAssocModalIsOpen}
				onOk={handleFieldAssocModalOk}
				onCancel={handleFieldAssocModalCancel}
			/>
		</>
	);
};

export default VectorDataContent;
